/**
 * The routes.
 *
 * `/` is deliberately not a dashboard. A visitor arriving from the marketing site has not
 * signed in and has nothing in a library, so the first screen is the thing they came for: drop
 * a file, read the report. Signing in changes what `/` renders, not where it sends you — a
 * redirect to `/login` before anything has happened is what the free incumbent does not do, and
 * the one behaviour the competitor research says costs the most visitors.
 *
 * A shared report at `/s/:token` renders outside the shell and outside the session entirely.
 * It is a client's or a label's view: no navigation, no upsell, no account.
 */

import { Suspense, lazy } from 'react';
import { Route, Routes } from 'react-router';

import { AppShell } from '@/components/AppShell';
import { Skeleton } from '@/components/ui/skeleton';
import { Toaster } from '@/components/ui/sonner';

const Analyse = lazy(() => import('@/routes/Analyse'));
const Home = lazy(() => import('@/routes/Home'));
const Tracks = lazy(() => import('@/routes/Tracks'));
const Report = lazy(() => import('@/routes/Report'));
const Compare = lazy(() => import('@/routes/Compare'));
const Versions = lazy(() => import('@/routes/Versions'));
const References = lazy(() => import('@/routes/References'));
const Albums = lazy(() => import('@/routes/Albums'));
const Settings = lazy(() => import('@/routes/Settings'));
const SignIn = lazy(() => import('@/routes/SignIn'));
const Shared = lazy(() => import('@/routes/Shared'));
const NotFound = lazy(() => import('@/routes/NotFound'));

function Loading() {
  return (
    <div className="mx-auto w-full max-w-5xl space-y-4 px-5 py-10">
      <Skeleton className="h-8 w-56" />
      <Skeleton className="h-64 w-full rounded-card" />
      <Skeleton className="h-40 w-full rounded-card" />
    </div>
  );
}

export function App() {
  return (
    <Suspense fallback={<Loading />}>
      <Toaster />
      <Routes>
        {/* Outside the shell: a link a client opens, and the sign-in step itself. */}
        <Route path="/s/:token" element={<Shared />} />
        <Route path="/sign-in" element={<SignIn />} />

        <Route
          path="*"
          element={
            <AppShell>
              <Suspense fallback={<Loading />}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/analyse" element={<Analyse />} />
                  <Route path="/tracks" element={<Tracks />} />
                  <Route path="/tracks/:id" element={<Report />} />
                  <Route path="/tracks/:id/compare" element={<Compare />} />
                  <Route path="/tracks/:id/versions" element={<Versions />} />
                  <Route path="/references" element={<References />} />
                  <Route path="/albums" element={<Albums />} />
                  <Route path="/settings/*" element={<Settings />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </AppShell>
          }
        />
      </Routes>
    </Suspense>
  );
}
